import React, { useState } from 'react';
import TextInput from '../../TextInput';
import SidePanel from '../../SidePanel.jsx';
import {
    ChevronRightIcon,
    Bars3Icon,
} from '@heroicons/react/24/outline/index.js';
import { formatCurrency } from '../../../lib/CurrencyHelpers.js';
import MoneyInput from '../../MoneyInput.jsx';
import DurationInput from '../../DurationInput.jsx';
import TextArea from '../../TextArea.jsx';
import { minutesToDuration } from '../../../lib/DateTime.js';
import IconSelectButton from '../../IconSelectButton.jsx';
import { Squares2X2Icon } from '@heroicons/react/24/solid';
import ErrorMessage from '../../ErrorMessage';
import OptionToggle from '../../OptionToggle.jsx';
import SortableList from '../../SortableList.jsx';
import IconSort from '../../icons/IconSort.jsx';

const AddonListItem = ({ value, onClick }) => {
    return (
        <div
            type="button"
            onClick={onClick}
            className="cursor-pointer group py-3 pl-4 pr-4 flex justify-between items-center"
        >
            <div className="flex items-start">
                <IconSort className="cursor-grab text-gray-500 inline-block -ml-8 opacity-0 group-hover:opacity-100" />
                <div>
                    <div>{value?.name || 'Unnamed Add-on'}</div>
                    <div className="text-gray-500 text-sm">
                        {value?.price > 0 && (
                            <span className="mr-4">
                                {formatCurrency(value?.price)}
                            </span>
                        )}
                        {value?.duration > 0 && (
                            <span>{minutesToDuration(value?.duration)}</span>
                        )}
                    </div>
                </div>
            </div>
            <ChevronRightIcon className="w-5 h-5 text-gray-300 group-hover:text-gray-500" />
        </div>
    );
};

const EditAddonPanel = ({
    value,
    setValue,
    isOpen,
    onClose,
    onDelete,
    settings,
}) => {
    const [nameError, setNameError] = useState(false);

    return (
        <SidePanel title="Edit Add-on" isOpen={isOpen} onClose={onClose}>
            <TextInput
                label="Name"
                id="name"
                required={true}
                autoFocus={true}
                value={value?.name}
                onChange={newValue => setValue({ ...value, name: newValue })}
            />
            {nameError && <ErrorMessage>Add-on name is required</ErrorMessage>}

            <MoneyInput
                label="Price"
                id="price"
                className="mt-4"
                value={value?.price}
                onChange={newValue => setValue({ ...value, price: newValue })}
            />

            <DurationInput
                label="Extra Duration (if booking event at same time)"
                className="mt-4"
                value={value?.duration}
                onChange={newValue =>
                    setValue({ ...value, duration: newValue })
                }
            />

            <TextArea
                label="Description"
                className="mt-4"
                value={value?.description}
                onChange={newValue =>
                    setValue({ ...value, description: newValue })
                }
            />

            {!!settings?.enableQuantity && (
                <div className="mt-4">
                    <TextInput
                        type="number"
                        inputClassName="!max-w-24"
                        label="Quantity Increment"
                        value={Math.max(value?.quantityIncrement || 1, 1)}
                        onChange={newValue =>
                            setValue({ ...value, quantityIncrement: newValue })
                        }
                    />
                    {!!value?.price && !!value?.name && (
                        <div className="text-sm mt-1 text-gray-500">
                            This is {formatCurrency(value?.price)} per{' '}
                            {value?.quantityIncrement} {value?.name}.
                            {value?.quantityIncrement > 1
                                ? ` People can select these in increments of ${value?.quantityIncrement}.`
                                : ''}
                        </div>
                    )}

                    <TextInput
                        type="number"
                        inputClassName="!max-w-24"
                        label="Max Quantity"
                        placeholder="No limit"
                        className="mt-4"
                        value={value?.maxQuantity || ''}
                        onChange={newValue =>
                            setValue({ ...value, maxQuantity: newValue ? Math.max(0, parseInt(newValue)) : null })
                        }
                    />
                    <div className="text-sm mt-1 text-gray-500">
                        Leave empty for no limit.
                    </div>
                </div>
            )}

            <div className="mt-4 flex items-center justify-between">
                <button
                    className="btn-save"
                    type="button"
                    onClick={() => {
                        setNameError(false);
                        if (!value?.name?.trim()) {
                            setNameError(true);
                            return;
                        }
                        onClose();
                    }}
                >
                    Save
                </button>
                <button
                    className="text-red-700"
                    type="button"
                    onClick={() => {
                        onDelete();
                        onClose();
                    }}
                >
                    Delete
                </button>
            </div>
        </SidePanel>
    );
};

const EditAddonForm = ({ settings, setSettings }) => {
    const [selectedAddon, setSelectedAddon] = useState(null);
    const addons = settings?.addons || [];
    const layout = settings?.layout;

    return (
        <div>
            <div className="panel pl-4 mt-4">
                <SortableList
                    items={addons.map((addon, idx) => ({ ...addon, id: idx }))}
                    onReorder={newItems => {
                        const reordered = newItems.map(({ id, ...rest }) => rest);
                        setSettings({ ...settings, addons: reordered });
                    }}
                    containerClassName="divide-y"
                    renderItem={({ item }) => (
                        <AddonListItem
                            value={item}
                            settings={settings}
                            onClick={() => setSelectedAddon(item.id)}
                        />
                    )}
                />
            </div>

            <button
                type="button"
                className="btn-light-pill mt-2"
                onClick={() => {
                    const newAddons = [...addons, {}];

                    setSettings({ ...settings, addons: newAddons });

                    setSelectedAddon(newAddons.length - 1);
                }}
            >
                Create New Add-On
            </button>

            <div className="input-label mt-4">Layout</div>
            <div className="flex items-center gap-x-4">
                <IconSelectButton
                    icon={
                        <Bars3Icon className="bg-white w-8 h-8 mx-3  border rounded" />
                    }
                    label="List"
                    isSelected={layout !== 'grid'}
                    onClick={() => setSettings({ ...settings, layout: 'list' })}
                />

                <IconSelectButton
                    icon={
                        <Squares2X2Icon className="bg-white w-8 h-8 mx-3 border rounded" />
                    }
                    label="Grid"
                    isSelected={layout === 'grid'}
                    onClick={() => setSettings({ ...settings, layout: 'grid' })}
                />
            </div>

            <div className="panel-label mt-4">Options</div>
            <div className="panel py-4 pl-4 divide-y">
                <OptionToggle
                    value={settings?.oncePerTransaction}
                    onChange={value =>
                        setSettings({ ...settings, oncePerTransaction: value })
                    }
                    title="Add only once (not per guest)"
                    description="If booking multiple spots then add-ons will only be added once per transaction instead of once per guest"
                    className="pr-4 mb-4"
                />

                <OptionToggle
                    value={settings?.enableQuantity}
                    onChange={value =>
                        setSettings({ ...settings, enableQuantity: value })
                    }
                    title="Allow Quantity"
                    description="Allow clients to purchase one or more of each item"
                    className="pr-4 pt-4"
                />
            </div>

            <EditAddonPanel
                value={
                    selectedAddon !== null
                        ? settings?.addons[selectedAddon]
                        : {}
                }
                setValue={newValue => {
                    const newAddons = [...addons];
                    newAddons[selectedAddon] = newValue;
                    setSettings({ ...settings, addons: newAddons });
                }}
                onDelete={() => {
                    const newAddons = [...addons];
                    newAddons.splice(selectedAddon, 1);
                    setSettings({ ...settings, addons: newAddons });
                }}
                settings={settings}
                isOpen={selectedAddon !== null}
                onClose={() => setSelectedAddon(null)}
            />
        </div>
    );
};

export default EditAddonForm;
